import { SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { showCustomSwal } from "helpers/swalHelper";
import {
  fetchWarehouse,
  updateStatusInWarehouse,
  updateStatusInWarehouseFiFo,
  WarehosuesxportsCSV,
} from "slices/Masters/warehosue/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import Swal from "sweetalert2";

import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";

import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";

const HEADER_BG_COLOR = "#f2f4f7";

const WarehouseList = () => {
  document.title = "Warehouse List";
  const dispatch = useDispatch<any>();
  const selectWarehouse = createSelector(
    (state: any) => state.warehouse,
    (warehouseState) => ({
      warehouse: warehouseState.warehouseList,
      rowCount: warehouseState.rowCount,
      loading: warehouseState.loading,
      error: warehouseState.error,
    })
  );

  const { warehouse, rowCount, loading, error } = useSelector(selectWarehouse);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.warehouse
  );

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.WAREHOUSE,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchWarehouse({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortDirection,
        search,
        filters: filterValue,
      })
    );
  }, [
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    dispatch,
  ]);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Handle status toggle
  const handleStatusToggle = useCallback(
    async (wareh: any, type: "status" | "fifo") => {
      const statusText = type === "status" ? "warehouse status" : "FIFO status";
      const updatedStatus =
        type === "status" ? !wareh.warehouse_status : !wareh.enable_FIFO;

      // Show loading modal
      showCustomSwal({
        title: "Updating...",
        text: `Please wait while we update the ${statusText}.`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        if (type === "status") {
          await dispatch(
            updateStatusInWarehouse({
              id: wareh.warehouse_id,
              warehouse_status: updatedStatus,
            })
          ).unwrap();
        } else if (type === "fifo") {
          await dispatch(
            updateStatusInWarehouseFiFo({
              id: wareh.warehouse_id,
              enable_FIFO: updatedStatus,
            })
          ).unwrap();
        }

        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Status Updated",
          text: `${statusText.charAt(0).toUpperCase() + statusText.slice(1)} ${
            updatedStatus ? "activated" : "deactivated"
          } successfully.`,
        });

        dispatch(
          fetchWarehouse({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortDirection,
            search,
            filters: filterValue,
          })
        );
      } catch (err: any) {
        console.error("Failed to update status", err);
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          `Failed to update ${statusText}`;

        // Replace loading modal with error modal
        showCustomSwal({
          icon: "error",
          title: "Update Failed",
          text: errorMessage,
        });
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      columnFilters,
    ]
  );

  const handleExport = async () => {
    const result = await WarehosuesxportsCSV({
      search,
      filters: filterValue,
    });

    // Add current date to filename
    const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    const filenameWithoutExt = result.filename.replace(/\.csv$/i, "");
    const filenameWithDate = `${filenameWithoutExt}_${currentDate}.csv`;

    return {
      ...result,
      filename: filenameWithDate,
    };
  };

  // Filter menu items to check if any will be visible
  // This ensures we only show the Action column if there are visible menu items
  const DynamicAction = canUpdate || canView
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          meta: {
            headerStyle: { backgroundColor: HEADER_BG_COLOR },
          },
          cell: ({ row }: { row: any }) => {
            const menuItems = [
              {
                icon: EditIcon,
                label: "Edit",
                permission: canUpdate,
                link: `/master/warehouse-forms/${row?.original?.warehouse_id}`,
                color: "primary",
              },
              {
                icon: SettingsIcon,
                label: "Configuration",
                permission: canUpdate,
                link: `/master/configure-warehouse/${row?.original?.warehouse_id}`,
                color: "primary",
              },
            ].filter((item) => item.permission === true);

            return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
          },
        },
      ]
    : [];

  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Warehouse Name",
        accessorKey: "warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.MASTERS.WAREHOUSE.FILTER_DROPDOWN(
          "warehouse_name"
        )}`,
      },
      {
        header: "Warehouse Code",
        accessorKey: "warehouse_code",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          headerStyle: { backgroundColor: HEADER_BG_COLOR },
        },
        cell: ({ row }: { row: any }) => {
          const org = row?.original;

          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(org, "status")}
            >
              <i
                className={`bi ${
                  org.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Address",
        accessorKey: "address",
        size: 150,
        maxSize: 150,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              fontSize: "0.8rem",
              width: 150,
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {row.original.address}
          </div>
        ),
      },

      {
        header: "Country",
        accessorKey: "country",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.MASTERS.WAREHOUSE.FILTER_DROPDOWN(
          "country"
        )}`,
      },
      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.MASTERS.WAREHOUSE.FILTER_DROPDOWN(
          "city"
        )}`,
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.MASTERS.WAREHOUSE.FILTER_DROPDOWN(
          "state"
        )}`,
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
      },
      {
        header: "Latitude",
        accessorKey: "latitude",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
      },
      {
        accessorKey: "longitude",
        header: "Longitude",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
      },
      // {
      //   header: "Status",
      //   accessorKey: "is_active",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   meta: {
      //     headerStyle: { backgroundColor: HEADER_BG_COLOR },
      //   },
      //   cell: ({ row }: { row: any }) => {
      //     const org = row?.original;

      //     return (
      //       <div
      //         style={{ fontSize: "1.5rem", cursor: "pointer" }}
      //         onClick={() => handleStatusToggle(org, "status")}
      //       >
      //         <i
      //           className={`bi ${
      //             org.is_active
      //               ? "bi-toggle-on text-success"
      //               : "bi-toggle-off text-danger"
      //           }`}
      //         />
      //       </div>
      //     );
      //   },
      // },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        meta: {
          headerStyle: { backgroundColor: HEADER_BG_COLOR },
        },
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original?.created_by_user?.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original?.updated_by_user?.username}
            date={row.original.updated_at}
          />
        ),
      },
      ...DynamicAction,
    ],

    [dispatch, handleStatusToggle, canUpdate]
  );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Warehouse</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                        <ExportButton onExport={handleExport} />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                        <ActionButton
                          label="Add Warehouse"
                          icon="bi bi-plus-circle"
                          navigateLink="/master/warehouse-form"
                          variant="primary"
                        />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <MasterTableContainer
                  columns={columns}
                  data={warehouse && warehouse.length > 0 ? warehouse : []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setFilterValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search warehouse..."
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default WarehouseList;
